import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import type { AdGroupActivityDataType } from '@/api/ad';
import AdGroupTableActionContent from '@/app/paid-search/AdGroupTableActionContent';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import type SolAPIError from '@/error/SolAPIError';
import DataTableAdvancedFiltersDialog from '@/features/AdvancedFiltersModal/DataTableAdvancedFiltersDialog';
import { DataTableMetricSelector } from '@/features/DataTableMetricSelector';

type Props = {
  columns: InstantSearchDataTableColumnDef<AdGroupActivityDataType>[];
  selectedAdGroups: AdGroupActivityDataType[];
  isMaxCompareLimit: boolean;
  data: AdGroupActivityDataType[] | undefined;
  totalResults: number | undefined;
  isLoading: boolean;
  error: SolAPIError | null;
  menuSelectorOpen: boolean;
  advancedFiltersOpen: boolean;
  onMetricSelectorOpen: (open: boolean) => void;
  onAdvancedFiltersOpen: (open: boolean) => void;
  onSelectedAdGroupsChange: (adGroups: AdGroupActivityDataType[]) => void;
  onScoreAdGroups: (adGroupIds: string[]) => void;
};

const PaidSearchAdGroupActivityTable = ({
  columns,
  selectedAdGroups,
  isMaxCompareLimit,
  data,
  totalResults,
  isLoading,
  error,
  menuSelectorOpen,
  advancedFiltersOpen,
  onMetricSelectorOpen,
  onAdvancedFiltersOpen,
  onSelectedAdGroupsChange,
  onScoreAdGroups,
}: Props) => {
  const selectedAdGroupIds = useMemo(
    () => selectedAdGroups.map((adGroup) => adGroup.adGroup.id),
    [selectedAdGroups],
  );
  const selectedAdGroupById = useMemo(
    () => new Map(selectedAdGroups.map((adGroup) => [adGroup.adGroup.id, adGroup])),
    [selectedAdGroups],
  );
  const visibleAdGroupById = useMemo(
    () => new Map((data ?? []).map((adGroup) => [adGroup.adGroup.id, adGroup])),
    [data],
  );

  const handleSelectRow = (adGroup: AdGroupActivityDataType) => {
    const isAdGroupSelected = selectedAdGroupIds.includes(adGroup.adGroup.id);

    if (!isAdGroupSelected && isMaxCompareLimit) {
      return;
    }

    if (isAdGroupSelected) {
      onSelectedAdGroupsChange(
        selectedAdGroups.filter((ag) => ag.adGroup.id !== adGroup.adGroup.id),
      );
      return;
    }

    onSelectedAdGroupsChange([...selectedAdGroups, adGroup]);
  };

  const handleSelectedAdGroupIdsChange = (selectedRowIds: string[]) => {
    const nextSelectedAdGroups = selectedRowIds.flatMap((adGroupId) => {
      const existingAdGroup = selectedAdGroupById.get(adGroupId);
      if (existingAdGroup != null) {
        return [existingAdGroup];
      }

      const visibleAdGroup = visibleAdGroupById.get(adGroupId);
      return visibleAdGroup != null ? [visibleAdGroup] : [];
    });

    onSelectedAdGroupsChange(nextSelectedAdGroups);
  };

  const handleRemoveSelectedAdGroup = (adGroup: AdGroupActivityDataType) => {
    onSelectedAdGroupsChange(selectedAdGroups.filter((c) => c !== adGroup));
  };

  return (
    <>
      <InstantSearchDataTable<AdGroupActivityDataType>
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        emptyMessage={<Trans>No ad groups found.</Trans>}
        error={error}
        getRowId={(record) => record.adGroup.id}
        initialColumnPinning={{
          start: ['adGroupId'],
        }}
        rowSelection={{
          selectedRowIds: selectedAdGroupIds,
          onSelectedRowIdsChange: handleSelectedAdGroupIdsChange,
          getRowCanSelect: (row) =>
            selectedAdGroupIds.includes(row.original.adGroup.id) || !isMaxCompareLimit,
          enableSelectAll: true,
          columnWidth: 40,
        }}
        actionContent={
          <AdGroupTableActionContent
            selectedAdGroups={selectedAdGroups}
            onRemoveSelectedAdGroup={handleRemoveSelectedAdGroup}
            onScoreAdGroups={onScoreAdGroups}
          />
        }
        onRow={(record) => ({ onClick: () => handleSelectRow(record) })}
      />
      <DataTableMetricSelector
        open={menuSelectorOpen}
        onClose={() => onMetricSelectorOpen(false)}
      />
      <DataTableAdvancedFiltersDialog
        open={advancedFiltersOpen}
        onClose={() => onAdvancedFiltersOpen(false)}
      />
    </>
  );
};

export default PaidSearchAdGroupActivityTable;
